import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./ReturnsData.module.css";

const ReturnsData = ({ rows, setRows, token, apiUrl, searchValue, selectedButton, setSelectedButton, font, revealHeaderButtons }) => {
	const [WMSOrderCreatedBlurb, setWMSOrderCreatedBlurb] = useState(false);
	const [WMSOrderBlurbIndex, setWMSOrderBlurbIndex] = useState(null);
	const [conditions, setConditions] = useState([]); //The condition options configured for this business's receiver
	const [deletedRows, setDeletedRows] = useState([]); //stack for undo button
	const [highlightedRows, setHighlightedRows] = useState([]); // store indices of highlighted rows
	const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
	const [showDeleteButtonIndex, setShowDeleteButtonIndex] = useState(null);
	const [viewableNumberOfRows, setViewableNumberOfRows] = useState(5);
	const [rowJustAdded, setRowJustAdded] = useState(false);
	const [showReceiverLoader, setShowReceiverLoader] = useState(true);
	const lastInputRef = useRef(null);
	const maxNumRows = 20;
	const containerRef = useRef(null); // for scrolling
	const navigate = useNavigate();
	const blankRow = {"pk": "",  "First Name": "", "Last Name": "", "Pkg": "", "Item Quantity": "", "Size": "", "Model": "", "Order Number": "", "CX notes": "", "Date": "", "Ship to address (exchanges)": "", "Barcode Label Tracking Number": "", "Condition": "",};

	// derive filtered data from rows + search. Runs when dependencies change and on every other re-render, React reuses the cached result. 
	const filteredData = useMemo(() => {
		const search = searchValue.toLowerCase();
	    return rows
	    	.filter((entry) => {
	        	const orderNum = entry["Order Number"]?.toLowerCase() || "";
	        	const trackingNum = entry["Barcode Label Tracking Number"]?.toLowerCase() || "";
	        	return orderNum.includes(search) || trackingNum.includes(search);
	     	 })
	      	.slice(-maxNumRows); // newest at bottom
	}, [rows, searchValue]);

	useEffect(() => { setTimeout(() => { setShowReceiverLoader(false); }, 2000)}, [])

	// adjust number of rows depending on screen size
	useEffect(() => {
		const handleResize = () => {
	    	if (window.innerWidth <= 481) { setViewableNumberOfRows(3); }
	    	else { setViewableNumberOfRows(5); }
	  	};
	  	handleResize();
	  	window.addEventListener("resize", handleResize);
	  	return () => window.removeEventListener("resize", handleResize);
	}, []);

	// focus tracking input only after a new row is added
	useEffect(() => {
		if (rowJustAdded && lastInputRef.current) {
	    	setTimeout(() => {
	      		lastInputRef.current.focus();
	      		setRowJustAdded(false); // reset so it doesn't trigger on other updates
	    	}, 300);
	  	}
	}, [filteredData, rowJustAdded]);

	//add a new blank row
	useEffect(() => {
		if (selectedButton === "New") {
			setRows((prev) => [...prev, { ...blankRow }]);
			setRowJustAdded(true);
			setSelectedButton(null);
			createNewReturn();
		}
	}, [selectedButton]);

	// Fetch conditions
	useEffect(() => {
		const fetchConditions = async () => {
		    try {
		    	const response = await fetch(`${apiUrl}/api/get_conditions/`, {
		        	method: "GET",
		        	headers: {"Content-Type": "application/json", Authorization: `Bearer ${token}`},
		      	});
		      const data = await response.json();
		      setConditions(data);
		    } catch (err) { console.log("error fetching conditions:", err); }
	  	};
		fetchConditions();
	}, [apiUrl, token]);

	//Create a new blank ReceivedReturn in db
	const createNewReturn = async () => {
		try { 
			const response = await fetch(`${apiUrl}/api/create_received_return/`, {
				method: "POST",
		        headers: {"Content-Type": "application/json", Authorization: `Bearer ${token}`},
			})
			const { new_received_return_pk } = await response.json();
			// attach pk to the last row we just appended
		    setRows((prev) => {
		    	const updated = [...prev];
		        updated[updated.length - 1] = { ...updated[updated.length - 1], pk: new_received_return_pk };
		        return updated;
		    });
		} catch (err) { console.log("error creating new row"); }
	}

	//delete row from ui and database
	const handleDeleteRow = (index) => {
		const rowToDelete = filteredData[index]; // row object
		setRows((prev) => {
			const rowIndex = prev.findIndex((r) => r.pk === rowToDelete.pk); // absolute index in rows
			if (rowIndex === -1) return prev; // safety
			const updated = prev.filter((r) => r.pk !== rowToDelete.pk);
			setDeletedRows((stack) => [...stack, { row: rowToDelete, rowIndex }]);
			deleteReceivedReturn(rowToDelete.pk);
			return updated;
		});
		setShowDeleteButtonIndex(null);
	};

	//delete received return from db
	const deleteReceivedReturn = async (pk) => {
		try {
			const response = await fetch(`${apiUrl}/api/delete_received_return/`, {
	        	method: "POST",
	          	headers: {"Content-Type": "application/json", Authorization: `Bearer ${token}`},
	          	body: JSON.stringify({ pk }),
	        });
		} catch (err) { console.log("error deleting received return."); }
	}

	//add a previously deleted row back to ui and database
	const handleUndo = () => {
		if (deletedRows.length === 0) return;
		const lastDeleted = deletedRows[deletedRows.length - 1]; //peek
		setDeletedRows((stack) => stack.slice(0, -1)); //pop
		setRows((prev) => {
			const updated = [...prev];
			// Clamp to array length in case data has shrunk
			const insertIndex = Math.min(lastDeleted.rowIndex, updated.length);
			updated.splice(insertIndex, 0, lastDeleted.row); //reinsert
			addBackReceivedReturn(lastDeleted.row);
			return updated;
		});
		// Highlight by absolute index (rowIndex in rows)
		setHighlightedRows((prev) => [...prev, lastDeleted.rowIndex]);
		setTimeout(() => { setHighlightedRows((prev) => prev.filter((i) => i !== lastDeleted.rowIndex)); }, 2000);
	};

	//add undo of received return back into db
	const addBackReceivedReturn = async (row) => {
		try {
			const response = await fetch(`${apiUrl}/api/add_back_received_return/`, {
	        	method: "POST",
	          	headers: {"Content-Type": "application/json", Authorization: `Bearer ${token}`},
	          	body: JSON.stringify({tracking_number: row["Barcode Label Tracking Number"], cx_notes: row["CX notes"], condition: row["Condition"], date: row["Date"], first_name: row["First Name"], item_quantity: row["Item Quantity"], last_name: row["Last Name"], model: row["Model"], order_id: row["Order Number"], pkg: row["Pkg"], ship_to_address: row["Ship to address (exchanges)"], size: row["Size"], pk: row["pk"]})
	        });
	        const result = await response.json();
        	setRows((prev) => prev.map((r) => r.pk === row.pk ? { ...r, pk: result.pk } : r));  // Replace row with matching old pk
		} catch (err) { console.log("error adding undone received return back into the database."); }
	}

	const handleInputChange = (index, field, value) => {
		const updated = [...filteredData];
		updated[index][field] = value;
		const first_name = updated[index]["First Name"];
		const last_name = updated[index]["Last Name"];
		setRows(updated);
		if ((field === "Barcode Label Tracking Number") && (value !== '' && !first_name && !last_name)) { getReceivedReturn(index, updated[index]); }
		else { updateReceivedReturn(updated[index]["pk"], field, value); }
	};

	//update model field of ReceivedReturn for whichever input the user changed
	const updateReceivedReturn = async (pk, field, value) => {
		try {
	        const response = await fetch(`${apiUrl}/api/update_received_return/`, {
	        	method: "POST",
	          	headers: {"Content-Type": "application/json", Authorization: `Bearer ${token}`},
	          	body: JSON.stringify({pk, field, value}),
	        });
	        if (response.status === 401) { navigate("/login"); } //if session timed out	
	    } catch (err) { console.error("Error updating received return:", err); }
	}

	//get return details associated with the tracking number provided and create a new received return
	const getReceivedReturn = async (index, row) => {
		try {
	        const response = await fetch(`${apiUrl}/api/get_received_return/`, {
	        	method: "POST",
	          	headers: {"Content-Type": "application/json", Authorization: `Bearer ${token}`},
	          	body: JSON.stringify({pk: row["pk"], tracking_number: row["Barcode Label Tracking Number"], order_id: row["Order Number"], first_name: row["First Name"], last_name: row["Last Name"], condition: row["Condition"]}),
	        });
	        if (response.status === 401) { navigate("/login"); } //if session timed out
	        const result = await response.json();
		    setRows((prev) => {
		    	const updated = [...prev];
		      	updated[index] = { ...updated[index], ...result }; // merge old + new fields
		      	return updated;
		    });
		    if (result === "new order") {
		    	setTimeout(() => { setWMSOrderCreatedBlurb(true); }, 500);
				setTimeout(() => { setWMSOrderCreatedBlurb(false); }, 3000);
			}
	    } catch (err) { console.error("Error fetching received return details:", err); }
	}

	const handleConditionClick = (index) => { setOpenDropdownIndex(openDropdownIndex === index ? null : index); };

	const handleConditionSelect = async (index, value) => {
		setRows((prev) => {
	    	const updated = [...prev];
	    	// Figure out which row in `rows` corresponds to this filtered index
	    	const rowToUpdate = filteredData[index];
	    	const rowIndex = prev.findIndex((r) => r.pk === rowToUpdate.pk);
	    	if (rowIndex !== -1) {
	      		updated[rowIndex] = { ...updated[rowIndex], Condition: value };
	      		updateReceivedReturn(updated[rowIndex].pk, "Condition", value);
	      		getReceivedReturn(rowIndex, updated[rowIndex]);
	    	}
	    	return updated;
	  	});
	  	setWMSOrderBlurbIndex(index);
	  	setOpenDropdownIndex(null); // close dropdown
	};

	return (
		<>
			<div ref={containerRef} className={`${styles.returns_data_container} fade_in`}>
				{!rows || showReceiverLoader ? (
					<div className={styles.receiver_data_loader_container}>
						<img className={styles.receiver_data_loader} src={`${process.env.PUBLIC_URL}/images/shipwheel_loader.gif`} alt="loading" />
					  	<p className={styles.receiver_data_loader_text}>Fetching data...</p>
					</div>
				) : (
					<table className={styles.returns_table}>
						<thead>
							<tr className={styles.header_row}>
								<th>Customer</th><th>Pkg</th><th>Qty</th><th>Size</th><th>Model</th><th>Order #</th><th>CX Notes</th><th>Date</th><th>Ship To Address</th><th>Tracking Number</th><th>Condition</th><th>Actions</th>
							</tr>
						</thead>
						<tbody>
								{filteredData.length === 0 ? (
									<tr className={styles.no_results_text}>
								    	<td colSpan={12}>
   											<div className={styles.no_results_box}>No results found.</div>
  										</td>
								    </tr>
								) : (
									filteredData.map((entry, index) => (
										<tr key={entry.pk || index} className={`fade_in ${styles.data_row} ${highlightedRows.includes(rows.findIndex((r) => r.pk === entry.pk)) ? styles.highlighted_row : ""}`}> 
											<td><strong><input type="text" className={`${styles.table_data_input} ${font}`} style={{ fontWeight: "bolder" }} value={entry["First Name"] ?? ""} placeholder="First name" onChange={(e) => handleInputChange(index, "First Name", e.target.value)} /></strong><br /><input type="text" className={`${styles.table_data_input} ${font}`} value={entry["Last Name"] ?? ""} placeholder="Last name" onChange={(e) => handleInputChange(index, "Last Name", e.target.value)} /></td>
											<td><span className={styles.pkg_yes}>Yes</span></td>
											<td><input type="text" className={`${styles.table_data_input} ${font}`} value={entry["Item Quantity"] ?? ""} placeholder="Item quantity" onChange={(e) => handleInputChange(index, "Item Quantity", e.target.value)} /></td>
											<td><input type="text" className={`${styles.table_data_input} ${font}`} value={entry["Size"] ?? ""} placeholder="Size" onChange={(e) => handleInputChange(index, "Size", e.target.value)} /></td>									
											<td>
												{(!entry["Model"] || openDropdownIndex === `model-${index}`) ? (<input type="text" className={`${styles.table_data_input} ${font}`} value={entry["Model"] ?? ""} placeholder="Model" onChange={(e) => handleInputChange(index, "Model", e.target.value)} onBlur={() => setOpenDropdownIndex(null)} />)
											  	: (<p className={styles.table_data_text} style={{ textAlign: "center" }} onMouseEnter={() => setOpenDropdownIndex(`model-${index}`)}>{entry["Model"]}</p>)}									 	
											</td>
											<td><input type="text" className={`${styles.table_data_input} ${font}`} style={{ fontWeight: "bolder" }} value={entry["Order Number"] ?? ""} placeholder="Order number" onChange={(e) => handleInputChange(index, "Order Number", e.target.value)} /></td>
											<td><input type="text" className={`${styles.table_data_input} ${font}`} value={entry["CX notes"] ?? ""} placeholder="CX notes" onChange={(e) => handleInputChange(index, "CX notes", e.target.value)} /></td>
											<td><input type="text" className={`${styles.table_data_input} ${font}`} value={entry["Date"] ?? ""} placeholder="Date" onChange={(e) => handleInputChange(index, "Date", e.target.value)} /></td>
											<td>
												{(!entry["Ship to address (exchanges)"] || openDropdownIndex === `ship-${index}`) ? (<input type="text" className={`${styles.table_data_input} ${font}`} value={entry["Ship to address (exchanges)"]} placeholder="Ship to address" onChange={(e) => handleInputChange(index, "Ship to address (exchanges)", e.target.value)} onBlur={() => setOpenDropdownIndex(null)} />)
											  : (<p className={styles.table_data_text} style={{ textAlign: "center" }} onMouseEnter={() => setOpenDropdownIndex(`ship-${index}`)}>{entry["Ship to address (exchanges)"]}</p>)}				  
											</td>
											<td><input type="text" className={`${styles.table_data_input} ${font}`} ref={index === filteredData.length - 1 ? lastInputRef : null} style={{ fontWeight: "bolder", textAlign: "left" }} value={entry["Barcode Label Tracking Number"]} placeholder="Scan tracking #" onChange={(e) => handleInputChange(index, "Barcode Label Tracking Number", e.target.value)} /></td>
											<td className={styles.condition_cell}>
												{WMSOrderCreatedBlurb && WMSOrderBlurbIndex === index && (
													<div className={styles.wms_order_blurb}>
														<p className={styles.wms_order_exclamation}>!</p>
														<p className={styles.wms_order_blurb_text}>Order created in WMS</p>
													</div>
												)}
												<span className={`${styles.conditions} ${entry["Condition"] === "Defective" ? styles.condition_defective : entry["Condition"] === "Signs of wear" ? styles.condition_warning : entry["Condition"] === "RTS" ? styles.condition_rts : entry["Condition"] === "Good" ? styles.condition_good : styles.condition_select}`} onClick={() => handleConditionClick(index)}>{entry["Condition"] || "Select"}</span>{openDropdownIndex === index && (<div className={styles.condition_dropdown}>{conditions.map((opt) => (<div key={opt} className={styles.dropdown_option} onClick={() => handleConditionSelect(index, opt)}>{opt}</div>))}</div>)}											
											</td>
											<td>{showDeleteButtonIndex === index ? (<div className={styles.delete_row_button_container}><img src={`${process.env.PUBLIC_URL}/images/remove_popup.png`} alt="remove_exchange_label_download" className={`${styles.remove_delete_button_container_button}`} onClick={() => { setShowDeleteButtonIndex(null) }} /><p className={styles.delete_row_button_container_text}>Delete this row?</p><div className={styles.delete_row_button} onClick={() => handleDeleteRow(index)}>Confirm</div></div>) : (<>👁️<span className={styles.action_icon_delete} title="Delete" onClick={() => setShowDeleteButtonIndex(index)}>🗑️</span></>)}</td>
										</tr>
									))
								)}
						</tbody>
					</table>
				)}
			</div>
			{filteredData && revealHeaderButtons && (
				<div className={`fade_in ${styles.returns_data_footer_container}`}>
			    	<p className={styles.left_footer_tag}>Showing {filteredData.length < viewableNumberOfRows ? filteredData.length : viewableNumberOfRows} of {filteredData.length}</p>
			    	<div className={styles.undo_button} style={{ opacity: deletedRows.length > 0 ? 1 : .3 }} onClick={handleUndo}>Undo</div>
			 	</div>
			)}
		</>
	);
};

export default ReturnsData;